import { ComponentPropsWithoutRef, FC } from 'react';

import useToggleState from '@/hooks/useToggleState';

import Flower from '@/assets/flower.svg?react';
import BlockOverlayContent from '@/components/layouts/BlockOverlayContent';
import Modal, {
  ModalContent,
  ModalDescription,
  ModalTitle,
} from '@/components/ui/Modal';
import cn from '@/utils/cn';

import Button, { ButtonLoading, ButtonRightArrow } from './Button';
import { useLingui } from '@lingui/react';

interface Props extends ComponentPropsWithoutRef<'div'> {
  surcharge: number;
  tax: number;
  total: number;
  loadingAccept?: boolean;
  onClickAccept?: () => void;
  loadingReject?: boolean;
  onClickReject?: () => void;
  heading?: string | React.ReactNode;
  subheading?: string | React.ReactNode;
  withButtons?: boolean;
}

const Summary: FC<Props> = ({
  surcharge,
  tax,
  total,
  loadingAccept,
  onClickAccept,
  loadingReject,
  onClickReject,
  className,
  heading,
  subheading,
  withButtons = true,
  ...props
}) => {
  const { i18n } = useLingui();

  const [dialogOpen, toggleDialogOpen] = useToggleState(false);

  return (
    <div
      className={cn(
        'flex max-h-max w-full flex-col gap-4 rounded-md bg-gray-200 px-3.5 py-4 text-dark-gray-500 lg:max-w-[440px] lg:p-9',
        !withButtons && 'lg:max-h-[324px]',
        className
      )}
      {...props}
    >
      {heading && <h2 className="h3 flex gap-2">{heading}</h2>}
      {subheading && (
        <h2 className="h4 flex items-center gap-2 text-blue-600">
          {subheading}
        </h2>
      )}
      <div className="h-px rounded-lg bg-gray-500" />
      <div className="flex flex-col gap-y-2.5 lg:py-3.5">
        <div className="caption-large flex justify-between gap-2">
          <p>{i18n.t('Wartość dopłaty')}</p>
          <p>
            {surcharge.toLocaleString('PL', {
              style: 'currency',
              currency: 'PLN',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}{' '}
          </p>
        </div>
        {tax > 0 && (
          <div className="caption-large flex justify-between gap-2">
            <p>{i18n.t('Podatek dochodowy (PIT)')}</p>
            <p>
              -{' '}
              {tax.toLocaleString('PL', {
                style: 'currency',
                currency: 'PLN',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}{' '}
            </p>
          </div>
        )}
      </div>
      <div className="h-px rounded-lg bg-gray-500" />
      <div className="caption-large flex items-center justify-between gap-2 lg:my-0">
        <p>{i18n.t('Razem')}</p>
        <p className="h2 flex items-center gap-2 text-blue-600">
          {' '}
          <Flower />
          {total.toLocaleString('PL', {
            style: 'currency',
            currency: 'PLN',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}
        </p>
      </div>
      {withButtons && (
        <>
          <Button
            onClick={onClickAccept}
            className="w-full lg:w-full"
            loading={loadingAccept}
            disabled={loadingAccept || loadingReject}
          >
            {i18n.t('Akceptuję propozycję')} <ButtonRightArrow />{' '}
            <ButtonLoading />
          </Button>
          <Button
            intent={'secondary'}
            onClick={toggleDialogOpen}
            loading={loadingReject}
            disabled={loadingAccept || loadingReject}
            className={cn('w-full py-2.5 lg:w-full lg:py-2.5')}
            data-testid="reject-offer"
          >
            {i18n.t('Porozmawiajmy o warunkach')}
            <ButtonLoading />
          </Button>
          {dialogOpen && (
            <Modal
              open
              onOpenChange={(open) => {
                if (!open) toggleDialogOpen();
              }}
            >
              <ModalContent className="max-w-[310px] gap-6 rounded-[28px] p-6 shadow-[0px_6px_16px_0px_rgba(0,0,0,0.16)] lg:max-w-[360px] lg:p-8">
                <BlockOverlayContent
                  title={i18n.t('Potwierdź swój wybór')}
                  info={i18n.t(
                    'W ciągu najbliższych 24 godzin skontaktujemy się z Tobą, aby omówić szczegóły i upewnić się, że propozycja odpowiada Twoim potrzebom. Jesteśmy tu, aby dostarczyć rozwiązanie, które Cię usatysfakcjonuje.'
                  )}
                  buttonConfirm={{
                    onClick: () => {
                      onClickReject?.();
                      toggleDialogOpen();
                    },
                    content: i18n.t('Tak potwierdzam'),
                  }}
                  onClose={toggleDialogOpen}
                  secondaryButtonContent={i18n.t('Wróć')}
                  titleAs={ModalTitle}
                  descriptionAs={ModalDescription}
                />
              </ModalContent>
            </Modal>
          )}
        </>
      )}
    </div>
  );
};

export default Summary;
